import { useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Building2, Edit, Trash } from "lucide-react";
import { DEPARTMENT_ALLOWED_SHIFTS, EMPLOYEE_DEPARTMENT, EMPLOYEE_SHIFT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, formatTimeRangeToAMPM, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { configBranchLabel, withConfigBranch } from "../../utils/branchSession";
import { MasterRecordStatusChip } from "../../utils/masterRecordStatus";

const EmployeeDepartemnt = () => {
    const { create, update, fetch } = useApiMethods();
    const [DepartmentsData, setDepartmentsData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null); // Track which row to delete
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } = checkUserPermissionFunc('master', 'department', 'SubMenu')

    // form state
    const [formData, setFormData] = useState({
        id: null,
        code: "",
        department_name: "",
        notes: "",
        is_active: true
    });
    const [shiftOptions, setShiftOptions] = useState([]);
    const [allowedShiftIds, setAllowedShiftIds] = useState([]);

    const loadShiftOptions = useCallback(async () => {
        const response = await fetch(EMPLOYEE_SHIFT, { drop_down: "True" });
        if (response?.status === "success") {
            setShiftOptions(response.data || []);
        } else {
            setShiftOptions([]);
        }
    }, [fetch]);

    const loadAllowedShifts = useCallback(async (departmentId) => {
        if (!departmentId) {
            setAllowedShiftIds([]);
            return;
        }
        const response = await fetch(DEPARTMENT_ALLOWED_SHIFTS, { department_id: departmentId });
        if (response?.status === "success") {
            const allowed = response?.data?.allowed_shifts || [];
            setAllowedShiftIds(allowed.map((row) => row.id));
        } else {
            setAllowedShiftIds([]);
        }
    }, [fetch]);

    useEffect(() => {
        if (!openDialog) return;
        loadShiftOptions();
        if (formData.id) {
            loadAllowedShifts(formData.id);
        } else {
            setAllowedShiftIds([]);
        }
    }, [openDialog, formData.id, loadShiftOptions, loadAllowedShifts]);

    const saveAllowedShifts = async (departmentId) => {
        if (!departmentId) return true;
        const response = await update(DEPARTMENT_ALLOWED_SHIFTS, {
            department_id: departmentId,
            shift_ids: allowedShiftIds,
        }, false);
        if (response?.status !== "success") {
            ToastMsg("error", response?.message || "Failed to save allowed shifts");
            return false;
        }
        return true;
    };

    // reset form & state
    const resetForm = () => {
        setFormData({ id: null, code: "", department_name: "", notes: "", is_active: true });
        setAllowedShiftIds([]);
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // table columns
    const DepartmentColumns = [
        {
            accessor: "code",
            Header: "Department Code",
            sortable: false,
            filterable: true,
        },
        {
            accessor: "branch_name",
            Header: "Branch",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "department_name",
            Header: "Department Name",
            sortable: true,
            filterable: true,
        },
        {
            accessor: "notes",
            Header: "Description",
            Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description")
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value, row }) => (
                <MasterRecordStatusChip
                    isActive={!!value}
                    isDraft={!!row?.original?.is_draft}
                />
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                Cell: ({ row }) => {
                    const handleOpenDialog = () => {
                        setEditMode(true);
                        setEditId(row?.code);
                        setFormData({
                            id: row?.id || null,
                            code: row?.code || "",
                            department_name: row?.department_name || "",
                            notes: row?.notes || "",
                            is_active: row?.is_active ?? true,
                        });
                        setOpenDialog(true);
                    };

                    const handleDeleteClick = (row) => {
                        setSelectedRow(row);
                        setIsDeleteConfirmOpen(true);
                    };

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenDialog}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>

                            {/* Delete Button (only if row is draft) */}
                            {row?.is_draft && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                    onClick={() => handleDeleteClick(row)}
                                >
                                    <Trash className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ] : [])
    ];

    // save as draft
    const handleDraft = async () => {
        try {
            setLoading(true);
            const payload = withConfigBranch({ ...formData, is_draft: true });
            const response = await create(EMPLOYEE_DEPARTMENT, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "User group saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            const payload = {
                ...selectedRow,
                draft_delete: true
            }
            // Use remove method for delete operations
            const response = await update(EMPLOYEE_DEPARTMENT, payload, false, setLoading);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft deleted successfully");
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft");
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setIsDeleteConfirmOpen(false);
        }
    };

    // save or update
    const handleSave = async () => {
        try {
            setLoading(true);
            let payload = withConfigBranch({ ...formData, is_draft: false });
            let response;

            if (editMode && editId) {
                // For update, use the specific endpoint with ID
                response = await update(EMPLOYEE_DEPARTMENT, payload);
            } else {
                response = await create(EMPLOYEE_DEPARTMENT, payload);
            }

            if (response?.status === "success") {
                const departmentId = formData.id || response?.data?.id;
                if (departmentId) {
                    const shiftsSaved = await saveAllowedShifts(departmentId);
                    if (!shiftsSaved) {
                        return;
                    }
                }
                ToastMsg("success", response.message || `User group ${editMode ? 'updated' : 'saved'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'save'} user group`);
            }
        } catch (error) {
            console.error("Error saving user group:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} user group`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContainerCard>
            {/* Header */}
            <ModuleTitleCard Icon={Building2} title={'Department'} subtitle={`Manage departments for ${configBranchLabel()}. A department saved here belongs only to this branch.`} totalRecords={totalRecords} buttonEnable={!!addPermission} setOpenDialog={setOpenDialog} buttonName={'Add Department'} />

            <Reactable
                columns={DepartmentColumns}
                data={DepartmentsData}
                setData={setDepartmentsData}
                setTotalCount={setTotalRecords}
                url={EMPLOYEE_DEPARTMENT}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? 'sm' : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add/Edit Modal */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit Department" : "Add Department"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="400px"
            >
                <form className="space-y-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Branch: <span className="font-medium text-gray-800 dark:text-gray-100">{configBranchLabel()}</span>
                    </p>
                    {/* Department Name */}
                    <div className="space-y-2">
                        <label
                            htmlFor="depatmentName"
                            className={cn(modalFieldLabelClass, "flex items-center gap-1")}
                        >
                            Department Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="depatmentName"
                            value={formData.department_name}
                            onChange={(e) =>
                                setFormData({ ...formData, department_name: e.target.value })
                            }
                            placeholder="Enter Department name"
                            required
                            disabled={loading}
                            className={cn(
                                "w-full rounded-lg transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Department Description */}
                    <div className="space-y-2">
                        <label
                            htmlFor="depatmentDescription"
                            className={modalFieldLabelClass}
                        >
                            Department Description
                        </label>
                        <Textarea
                            id="depatmentDescription"
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                            placeholder="Enter Department description"
                            disabled={loading}
                            {...modalDescriptionFieldProps}
                            className={modalTextareaClass}
                        />
                    </div>

                    <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Allowed shifts
                            </label>
                            <Select
                                mode="multiple"
                                allowClear
                                className="w-full"
                                placeholder="Leave empty to allow all company shifts"
                                value={allowedShiftIds}
                                onChange={setAllowedShiftIds}
                                disabled={loading}
                                options={shiftOptions.map((shift) => ({
                                    value: shift.id,
                                    label: shift.shift_timings_from
                                        ? `${shift.shift_name} (${formatTimeRangeToAMPM(shift.shift_timings_from, shift.shift_timings_to)})`
                                        : shift.shift_name,
                                }))}
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                When set, onboarding, duty roster, and shift-change pickers for this department show only these shifts.
                            </p>
                        </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label
                            htmlFor="is_active"
                            className={modalFieldLabelClass}
                        >
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading || !editMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading || !editMode}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="text-red-500">*</span> indicates required field
                    </p>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft Group"
                description="Are you sure you want to delete this draft group? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default EmployeeDepartemnt;